import moment from 'moment';

/**
 * Due-date semantics of to-dos, same as the backend helper in shrd (utils/helpers/toDoDue.helper.ts):
 * with hasDueTime the to-do is due at dueDate, otherwise it is due for the whole day and only overdue once the day is over.
 */
export interface ToDoDueLike {
  dueDate?: Date | string | null;
  hasDueTime?: boolean | null;
  completed?: boolean;
}

export type ToDoDueState = 'none' | 'overdue' | 'today' | 'soon' | 'later';

/** Moment from which the to-do counts as overdue */
export function toDoDeadline(item: ToDoDueLike): moment.Moment | null {
  if (!item.dueDate) {
    return null;
  }
  return item.hasDueTime ? moment(item.dueDate) : moment(item.dueDate).startOf('day').add(1, 'day');
}

export function isToDoOverdue(item: ToDoDueLike, now: moment.Moment = moment()): boolean {
  const deadline = toDoDeadline(item);
  return !!deadline && !item.completed && !deadline.isAfter(now);
}

/** Due today or earlier and not done */
export function isToDoDue(item: ToDoDueLike, now: moment.Moment = moment()): boolean {
  return !!item.dueDate && !item.completed && moment(item.dueDate).isSameOrBefore(now.clone().endOf('day'));
}

export function toDoDueState(item: ToDoDueLike, now: moment.Moment = moment()): ToDoDueState {
  if (!item.dueDate || item.completed) {
    return 'none';
  }
  if (isToDoOverdue(item, now)) {
    return 'overdue';
  }
  if (moment(item.dueDate).isSame(now, 'day')) {
    return 'today';
  }
  return moment(item.dueDate).isSameOrBefore(now.clone().add(7, 'days').endOf('day')) ? 'soon' : 'later';
}

/** Tone class of modern.css for the due state */
export function toDoDueTone(item: ToDoDueLike, now: moment.Moment = moment()): string {
  switch (toDoDueState(item, now)) {
    case 'overdue':
      return 'tone-bad';
    case 'today':
      return 'tone-warn';
    case 'soon':
      return 'tone-info';
    default:
      return 'tone-muted';
  }
}

/** Time part, only when the to-do has one */
export function formatToDoTime(item: ToDoDueLike): string {
  return item.dueDate && item.hasDueTime ? moment(item.dueDate).format('HH:mm') : '';
}

/**
 * Human readable due date: "heute", "morgen 14:30", "Mo 12.10.", "12.10.2027 09:00"
 * @param style short: without weekday/year when close; long: always full date
 */
export function formatToDoDue(item: ToDoDueLike, style: 'short' | 'long' = 'short', now: moment.Moment = moment()): string {
  if (!item.dueDate) {
    return '';
  }
  const due = moment(item.dueDate);
  const time = formatToDoTime(item);
  const withTime = (text: string) => (time ? `${text}, ${time} Uhr` : text);
  if (style === 'long') {
    return withTime(due.format('dd, DD.MM.YYYY'));
  }
  const days = due.clone().startOf('day').diff(now.clone().startOf('day'), 'days');
  if (days === 0) {
    return withTime('heute');
  }
  if (days === 1) {
    return withTime('morgen');
  }
  if (days === -1) {
    return withTime('gestern');
  }
  if (Math.abs(days) < 7) {
    return withTime(due.format('dd, DD.MM.'));
  }
  return withTime(due.format(due.isSame(now, 'year') ? 'DD.MM.' : 'DD.MM.YYYY'));
}

/** Combines a day and an optional "HH:mm" time into the stored due date (local midnight without time) */
export function combineDueDate(day: Date | string | null | undefined, time?: string | null): Date | null {
  if (!day) {
    return null;
  }
  const result = moment(day).startOf('day');
  const match = time ? /^(\d{1,2}):(\d{2})$/.exec(time) : null;
  if (match) {
    result.hours(Number(match[1])).minutes(Number(match[2]));
  }
  return result.toDate();
}
